import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { CreateFAQDialogComponent } from './create-faq-dialog/create-faq-dialog.component';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-faqs',
  templateUrl: './faqs.component.html',
  styleUrls: ['./faqs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FAQsComponent implements OnInit {
  AppConstants = AppConstants;
  faqs: any[] = [];
  tableColumns: TableColumn[] = [];
  showConfirmDialog: boolean = false;
  faqToDelete: any = null;

  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private _router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private toasterService = inject(ToasterService);

  ngOnInit(): void {
    this.initializeTableColumns();
    this.fetchFAQs();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        iconPath: 'assets/icons/edit-icon.svg',
        action: 'edit',
        title: 'Edit'
      },
      {
        iconPath: 'assets/icons/delete-icon.svg',
        action: 'delete',
        title: 'Delete'
      }
    ];

    this.tableColumns = [
      {
        field: 'faqId',
        header: 'FAQ ID',
        type: 'text',
        sortable: true
      },
      {
        field: 'linkedTender',
        header: 'Linked Tender',
        type: 'linked-tender',
        sortable: true
      },
      {
        field: 'question',
        header: 'Question',
        type: 'text',
        sortable: true
      },
      {
        field: 'answer',
        header: 'Answer',
        type: 'text',
        sortable: true
      },
      {
        field: 'createdBy',
        header: 'Created By',
        type: 'text',
        sortable: true
      },
      {
        field: 'createdDate',
        header: 'Created Date',
        type: 'date',
        format: 'MMM dd, yyyy',
        sortable: true
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeSize: 'medium',
        sortable: true
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: actionIcons,
        sortable: false
      }
    ];
  }

  fetchFAQs(): void {
    this._loaderService.showLoader();
    
    this.apiService.setBaseUrl(environment.API_URL);
    
    this.apiService.get(
      AppConstants.CONTROLLERS.FAQs,
      AppConstants.ACTIONS.GetAllFAQs,
      true,
      environment.APIs_PREFIX
    ).pipe(
      map((response: any) => {
        if (response?.data && Array.isArray(response.data)) {
          return this.mapFAQsResponse(response.data);
        } else if (Array.isArray(response)) {
          return this.mapFAQsResponse(response);
        }
        return [];
      }),
      catchError((error) => {
        console.error('Failed to fetch FAQs:', error);
        this.toasterService.showError('Failed to load FAQs', 'Error');
        return of([]);
      })
    ).subscribe({
      next: (faqs) => {
        this.faqs = faqs;
        this.cdr.markForCheck();
        this._loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching FAQs:', error);
        this.faqs = [];
        this.cdr.markForCheck();
        this._loaderService.hideLoader();
      }
    });
  }

  private mapFAQsResponse(data: any[]): any[] {
    return data.map((item: any) => ({
      id: item.id || item.ID || 0,
      faqId: item.faqId || item.FAQId || '',
      linkedTenderId: item.linkedTenderId || item.LinkedTenderId || '',
      linkedTender: item.linkedTenderId ? {
        title: item.linkedTenderTitle || item.tender?.tenderName || item.tender?.TenderName || '',
        tenderId: item.linkedTenderId || item.LinkedTenderId || ''
      } : null,
      question: item.question || item.Question || '',
      answer: item.answer || item.Answer || '',
      createdBy: item.createdBy || item.CreatedBy || '',
      createdDate: item.createdDate || item.CreatedDate ? new Date(item.createdDate || item.CreatedDate) : null,
      status: item.status || item.Status || 'Draft'
    }));
  }

  onTableActionClick(event: { action: string, row: any }): void {
    if (event.action === 'view') {
      this._router.navigate([AppConstants.ROUTES.FAQS, AppConstants.ROUTES.VIEW_FAQ, event.row.faqId]);
    } else if (event.action === 'edit') {
      this.editFAQ(event.row);
    } else if (event.action === 'delete') {
      this.deleteFAQ(event.row);
    }
  }

  onTableLinkClick(event: { field: string, row: any }): void {
    if (event.field === 'linkedTender' && event.row.linkedTender?.tenderId) {
      this.viewTender(event.row.linkedTender.tenderId);
    }
  }

  viewTender(tenderId: string): void {
    this._router.navigate([`/${AppConstants.ROUTES.TENDER}/${AppConstants.ROUTES.TENDERDETAILS}/${tenderId}`]);
  }

  private editFAQ(faq: any): void {
    const dialogRef = this.dialog.open(CreateFAQDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'create-faq-dialog',
      data: { faq: faq } // Pass FAQ data for editing
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchFAQs();
      }
    });
  }

  private deleteFAQ(faq: any): void {
    this.faqToDelete = faq;
    this.showConfirmDialog = true;
    this.cdr.markForCheck();
  }

  cancelDeleteFAQ(): void {
    this.showConfirmDialog = false;
    this.faqToDelete = null;
    this.cdr.markForCheck();
  }

  confirmDeleteFAQ(): void {
    if (!this.faqToDelete) {
      this.showConfirmDialog = false;
      return;
    }

    this.showConfirmDialog = false;
    this._loaderService.showLoader();
    
    const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.DeleteFAQ}/${this.faqToDelete.id || this.faqToDelete.ID}`;
    
    this.apiService.deleteByUrl(url, {}, {}).subscribe({
      next: (response: any) => {
        this._loaderService.hideLoader();
        if (response && (response.IsSuccess || response.isSuccess || response.data)) {
          this.toasterService.showSuccess('FAQ deleted successfully', 'Success');
          this.fetchFAQs();
        } else {
          const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'Failed to delete FAQ';
          this.toasterService.showError(errorMessage, 'Error');
        }
        this.faqToDelete = null;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this._loaderService.hideLoader();
        const errorMessage = error?.error?.ErrorMessageEn || 
                            error?.error?.errorMessageEn || 
                            error?.error?.message || 
                            error?.message || 
                            'An error occurred while deleting the FAQ';
        this.toasterService.showError(errorMessage, 'Error');
        console.error('[FAQs] Error deleting FAQ:', error);
        this.faqToDelete = null;
        this.cdr.markForCheck();
      }
    });
  }

  getConfirmDialogMessage(): string {
    const faqIdDisplay = this.faqToDelete?.faqId || this.faqToDelete?.id || '';
    return `Are you sure you want to delete FAQ "${faqIdDisplay}"?`;
  }

  createFAQ(): void {
    const dialogRef = this.dialog.open(CreateFAQDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      panelClass: 'create-faq-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchFAQs();
      }
    });
  }

  exportFAQs(): void {
    console.log('Export FAQs');
    // TODO: Implement export functionality
  }
}
